import { atom, computed } from 'nanostores';
import { COMMANDS, PROMPT_CATEGORIES } from './command/config';
import { GroupCommand } from './command/models/GroupCommand';
import { PromptCommandCategory } from './command/models/PromptCommandCategory';
import { UserPromptCommand } from './command/models/UserPromptCommand';
import {
  type AnyCommand,
  type RawPromptCommand,
  type PromptCommandCategorySerialized,
  type SearchItem,
} from './command/types';
import { settingsService } from '~/lib/services/settings-service';
import { commandPaletteStore } from '~/lib/stores/settings';
import { hmrPropertyFactory } from '~/utils/hmr';

export interface CommandGroup {
  id: string;
  name: string;
  commands: {
    index: number;
    command: AnyCommand;
  }[];
}

const hmr = hmrPropertyFactory('CommandStore');

export class CommandStore {
  @hmr()
  $selectedIndexStack = atom<number[]>([0]);

  @hmr()
  $routeStack = atom<AnyCommand[]>([]);

  @hmr()
  $isShortcutsDropdownOpen = atom(false);

  @hmr()
  router = {
    clear: () => {
      this.$routeStack.set([]);
      this.$selectedIndexStack.set([]);
    },
    push: (command: AnyCommand) => {
      this.$routeStack.set([...this.$routeStack.get(), command]);
    },
    pop: () => {
      this.$routeStack.set(this.$routeStack.get().slice(0, -1));

      const level = this.$selectedIndexStackLevel.get() + 1;

      this.$selectedIndexStack.set(this.$selectedIndexStack.get().slice(0, level));
    },
  };

  $customPromptCategories = computed([commandPaletteStore], (settings) => {
    return settings?.customPromptCategories?.map((category) => new PromptCommandCategory(category)) || [];
  });

  $customPrompts = computed([commandPaletteStore], (settings) => {
    return settings.customPrompts.map((prompt) => {
      const category = this.findPromptCategoryById(prompt.category);

      return new UserPromptCommand({
        id: prompt.id,
        name: prompt.name,
        icon: prompt.icon,
        prompt: prompt.prompt,
        category,
      });
    });
  });

  $favoritedCommands = computed([commandPaletteStore], (settings) => settings.favoritedCommands || []);

  $selectedCommandIndex = computed(this.$selectedIndexStack, (selectedIndexStack) => {
    return selectedIndexStack.at(-1) ?? 0;
  });

  $promptCategories = computed(this.$customPromptCategories, (customPromptCategories) => [
    ...customPromptCategories,
    ...Object.values(PROMPT_CATEGORIES),
  ]);

  $root = computed([this.$customPrompts, this.$favoritedCommands], (customPrompts) => {
    const commands = Object.values(customPrompts).map((command) => new UserPromptCommand(command));
    return COMMANDS.withCommands(commands);
  });

  $selectedIndexStackLevel = computed([this.$routeStack, this.$root], (routeStack) => {
    return routeStack.filter((item) => item instanceof GroupCommand).length - 1;
  });

  $route = computed([this.$routeStack, this.$root], (routeStack, root) => {
    const last = routeStack.at(-1);

    if (!last || last.id === root.id) {
      return root;
    }

    return this.#findCommandById(last.id, root.commands) || last;
  });

  #findCommandById = (targetId: string, commands: AnyCommand[]): AnyCommand | undefined => {
    const foundCommand = commands.find((command) => command.id === targetId);

    if (foundCommand) {
      return foundCommand;
    }

    for (const command of commands) {
      if (command instanceof GroupCommand && command.commands) {
        const nestedResult = this.#findCommandById(targetId, command.commands);

        if (nestedResult) {
          return nestedResult;
        }
      }
    }

    return undefined;
  };

  findPromptCategoryById(id?: string) {
    if (!id) {
      return undefined;
    }

    return this.$promptCategories.get().find((category) => category.id === id);
  }

  getActiveIndex() {
    return this.$selectedCommandIndex.get() ?? 0;
  }

  @hmr()
  autoScrollToSelectedItem = atom(false);

  setActiveIndex(index: number | ((index: number) => number), { autoScroll = true }: { autoScroll?: boolean } = {}) {
    this.autoScrollToSelectedItem.set(autoScroll);

    const stack = [...this.$selectedIndexStack.get()];

    stack[this.$selectedIndexStackLevel.get()] = typeof index === 'function' ? index(this.getActiveIndex()) : index;

    this.$selectedIndexStack.set(stack);
  }

  nextCommand(commands: SearchItem[]) {
    const nextIndex = (this.getActiveIndex() + 1) % commands.length;
    this.setActiveIndex(nextIndex);
  }

  nextGroup(commands: SearchItem[], groups: CommandGroup[]) {
    const groupWithCurrentCommand = groups.find((group) =>
      group.commands.find((command) => command.index === this.getActiveIndex()),
    );

    if (!groupWithCurrentCommand) {
      return;
    }

    const nextGroup = groups[(groups.indexOf(groupWithCurrentCommand) + 1) % groups.length];
    const nextCommandIndex = commands.find((item) => item.index === nextGroup.commands[0].index);

    this.setActiveIndex(nextCommandIndex?.index ?? 0);
  }

  previousGroup(commands: SearchItem[], groups: CommandGroup[]) {
    const activeIndex = this.getActiveIndex();

    const groupWithCurrentCommand = groups.find((group) => group.commands.find((item) => item.index === activeIndex));

    if (!groupWithCurrentCommand) {
      return;
    }

    const prevGroup = groups[(groups.indexOf(groupWithCurrentCommand) - 1 + groups.length) % groups.length];
    const prevCommandIndex = commands.find((item) => item.index === prevGroup.commands[0].index);

    this.setActiveIndex(prevCommandIndex?.index ?? 0);
  }

  previousCommand(commands: SearchItem[]) {
    const currentIndex = this.getActiveIndex();
    const prevIndex = currentIndex === 0 ? commands.length - 1 : currentIndex - 1;
    this.setActiveIndex(prevIndex);
  }

  addCustomPrompt(customPrompt: Omit<RawPromptCommand, 'id'>) {
    const prompts = commandPaletteStore.get().customPrompts;

    const updatedPrompt = {
      id: crypto.randomUUID(),
      name: customPrompt.name,
      icon: customPrompt.icon,
      prompt: customPrompt.prompt,
      category: customPrompt.category,
    };

    const updatedPrompts = [...prompts, updatedPrompt];

    settingsService.set('commandPalette', 'customPrompts', updatedPrompts);

    return updatedPrompt;
  }

  removeCustomPrompt(promptId: string) {
    const prompts = commandPaletteStore.get().customPrompts;
    const updatedPrompts = prompts.filter((prompt) => prompt.id !== promptId);

    settingsService.set('commandPalette', 'customPrompts', updatedPrompts);
  }

  updateCustomPrompt(promptId: string, values: Omit<RawPromptCommand, 'id'>) {
    const prompts = commandPaletteStore.get().customPrompts;
    const updatedPrompts = prompts.map((prompt) => (prompt.id === promptId ? { ...prompt, ...values } : prompt));

    settingsService.set('commandPalette', 'customPrompts', updatedPrompts);
  }

  addCustomPromptCategory(category: Omit<PromptCommandCategorySerialized, 'id'>) {
    const categories = commandPaletteStore.get().customPromptCategories;

    const newCategory = {
      id: crypto.randomUUID(),
      name: category.name,
      icon: category.icon,
    };

    const updatedCategories = [...categories, newCategory];

    settingsService.set('commandPalette', 'customPromptCategories', updatedCategories);

    return newCategory;
  }

  toggleFavorite(commandId: string) {
    const favoritedCommands = commandPaletteStore.get().favoritedCommands || [];
    const isFavorited = this.isFavorited(commandId);

    const updatedFavorites = isFavorited
      ? favoritedCommands.filter((id) => id !== commandId)
      : [...favoritedCommands, commandId];

    settingsService.set('commandPalette', 'favoritedCommands', updatedFavorites);

    this.setActiveIndex((index) => (isFavorited ? Math.max(0, index - 1) : index + 1), {
      autoScroll: false,
    });
  }

  isFavorited(commandId: string) {
    return this.$favoritedCommands.get().includes(commandId);
  }

  getCategoryById(id?: string) {
    return this.$promptCategories.get().find((category) => category.id === id);
  }
}

export const commandStore = new CommandStore();
